import { m } from "@nerves/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Page, Panel } from "@/components/shell/Page";
import { Avatar, Badge, type BadgeTone, InlineError, RelativeTime, RowSkeleton, SectionHeader } from "@/components/ui";
import { adminQuery, loadMe } from "@/lib/queries";

export const Route = createFileRoute("/_app/admin")({
  beforeLoad: async ({ context }) => {
    if (!(await loadMe(context.queryClient))?.isAdmin) throw redirect({ to: "/" });
  },
  component: AdminPage,
});

function Row({
  title,
  tone,
  state,
  children,
}: {
  title: string;
  tone: BadgeTone;
  state: string;
  children?: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-start gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <span className="min-w-40 flex-1 text-sm font-medium">{title}</span>
      <div className="flex flex-col items-end gap-1 text-right text-xs text-fg-muted">
        <Badge tone={tone}>{state}</Badge>
        {children}
      </div>
    </li>
  );
}

function AdminPage() {
  const query = useQuery(adminQuery);
  if (query.isPending)
    return (
      <Page title={m.admin_title()}>
        <RowSkeleton rows={4} />
      </Page>
    );
  if (query.isError) {
    return (
      <Page title={m.admin_title()}>
        <InlineError message={m.error_generic()} onRetry={() => void query.refetch()} />
      </Page>
    );
  }
  const s = query.data;
  return (
    <Page title={m.admin_title()}>
      <Panel>
        <ul>
          <Row
            title={m.admin_discord()}
            tone={!s.discord.configured ? "neutral" : s.discord.connected ? "success" : "danger"}
            state={
              !s.discord.configured
                ? m.admin_not_configured()
                : s.discord.connected
                  ? m.admin_connected()
                  : m.admin_disconnected()
            }
          >
            <span>{m.admin_sessions({ n: s.mcpSessions })}</span>
          </Row>
          <Row
            title={m.admin_plannello()}
            tone={!s.plannello.configured ? "neutral" : s.plannello.error ? "danger" : "success"}
            state={
              !s.plannello.configured
                ? m.admin_not_configured()
                : s.plannello.error
                  ? m.error_title()
                  : m.admin_connected()
            }
          >
            {s.plannello.error ? <span>{s.plannello.error}</span> : null}
            {s.plannello.syncedAt ? <RelativeTime value={s.plannello.syncedAt} /> : null}
          </Row>
          <Row
            title={m.admin_queue()}
            tone={s.deliveries.failed ? "danger" : s.deliveries.pending ? "warning" : "success"}
            state={
              s.deliveries.failed
                ? m.admin_failed({ n: s.deliveries.failed })
                : s.deliveries.pending
                  ? m.admin_pending({ n: s.deliveries.pending })
                  : m.admin_queue_ok()
            }
          >
            {s.deliveries.lastError ? (
              <span className="max-w-md break-words">{m.admin_last_error({ error: s.deliveries.lastError })}</span>
            ) : null}
          </Row>
        </ul>
      </Panel>
      <section aria-labelledby="people-title" className="flex flex-col gap-3">
        <SectionHeader id="people-title" title={m.admin_people()} />
        <Panel>
          <ul>
            {s.people.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-b-0"
              >
                <Avatar name={p.name} seed={p.id} size="lg" labelled={false} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{p.name}</span>
                  <span className="truncate text-xs text-fg-muted">{p.email}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {p.isAdmin ? <Badge tone="accent">{m.admin_operator()}</Badge> : null}
                  <Badge tone={p.discordLinked ? "success" : "neutral"}>
                    {p.discordLinked ? m.admin_discord_linked() : m.admin_discord_missing()}
                  </Badge>
                  {p.lastAgentActivity ? (
                    <span className="text-xs text-fg-subtle">
                      {m.admin_last_agent({ time: "" }).trim()} <RelativeTime value={p.lastAgentActivity} />
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </section>
    </Page>
  );
}
